import { Modal, Form, Input, Button, message, Select, Checkbox, DatePicker, Tag, Alert, Descriptions } from "antd";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { API } from "../../../config/API";
import { GET } from "../../../utils/apirequest";
import {
  filterPlansForViewer,
  getUserRoleId,
} from "../../../utils/subscriptionPlans";
import axios from "../../../config/axiosInstance";
import { countryHasZones, zonesForCountry } from "../../../config/zones";
import {
  DEFAULT_COUNTRY,
  activeCountries,
  formatMoney,
  getCountry,
  missingPriceMessage,
  normalizeCountry,
  resolvePlanPrice,
} from "../../../config/countries";
import { useEpaperConfig } from "../../../config/epaperConfig";
import DialCodeInput, {
  dialCodeRule,
  normalizePhoneDigits,
  phoneNumberRule,
} from "./dialCodeInput";
import dayjs from "dayjs";

type ExistingEpaperUser = {
  id: number;
  full_name: string;
  email: string | null;
  phone: string | null;
  amount: string | null;
  currency?: string | null;
  country?: string | null;
  isPaid: boolean;
  status: "active" | "inactive" | "suspended" | null;
  subscriber_code: string | null;
  zone: string | null;
  register_mode: string | null;
  agent_code: string | null;
  agent_name: string | null;
  current_plan_id: number | null;
  current_plan_type: string | null;
  subscription_status: string | null;
  subscription_start_date: string | null;
  subscription_end_date: string | null;
  last_paid_amount: number | null;
  latest_subscription_id: number | null;
  email_verified: boolean;
  last_login: string | null;
  createdAt: string | null;
};

type Plan = {
  id: number;
  plan_name: string;
  type: string;
  price: number | string;
  country_prices?: Record<string, { currency?: string; price: number | string }> | null;
  duration_type: "days" | "months" | "years";
  duration_value: number;
  region?: "india" | "gulf" | null;
  status?: string;
  is_active?: boolean;
  allowed_role_ids?: number[] | null;
};

const addPlanDuration = (start: dayjs.Dayjs, plan: Plan): dayjs.Dayjs => {
  const v = Number(plan.duration_value) || 0;
  if (!v) return start;
  switch (plan.duration_type) {
    case "days":
      return start.add(v, "day");
    case "months":
      return start.add(v, "month");
    case "years":
      return start.add(v, "year");
    default:
      return start;
  }
};

const paymentModes = [
  { value: "cash", label: "Cash" },
  { value: "online", label: "Online (PayTabs)" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cheque", label: "Cheque" },
  { value: "upi", label: "UPI" },
];

const CreateModal = (props: any) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [useDefaultPassword, setUseDefaultPassword] = useState(true);
  const User = useSelector((state: any) => state?.User);
  const isAdmin = User?.user?.role?.id === 4;
  const viewerRoleId = getUserRoleId(User?.user);

  const [plans, setPlans] = useState<Plan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);

  // Countries, zones and dial codes come from E-Paper Settings; reloaded
  // each time the modal opens so settings changes show up immediately.
  useEpaperConfig(props?.open);

  // Country defaults to the logged-in agent's country (UAE when unset, or
  // the first active country if that one is disabled) and drives the
  // amount/currency, zone and dial code. The backend re-prices on submit.
  const agentCountry = normalizeCountry(User?.user?.country) || DEFAULT_COUNTRY;
  const defaultCountry =
    activeCountries().some((c) => c.code === agentCountry)
      ? agentCountry
      : activeCountries()[0]?.code || DEFAULT_COUNTRY;
  const selectedCountry: string =
    Form.useWatch("country", form) || defaultCountry;
  const [currency, setCurrency] = useState(getCountry(defaultCountry).currency);
  const hasZones = countryHasZones(selectedCountry);
  const selectedPlan = plans.find((p) => p.id === selectedPlanId) || null;
  const planPrice = resolvePlanPrice(selectedPlan, selectedCountry);

  // Rich duplicate-match state — when the identity check finds an existing
  // subscriber, we show a detailed confirmation panel (not a plain confirm)
  // with enough info for the admin to verify the person before the upgrade
  // hand-off. typedEmail/typedPhone let us flag partial matches.
  const [duplicateMatch, setDuplicateMatch] = useState<{
    user: ExistingEpaperUser;
    typedEmail: string | null;
    typedPhone: string | null;
  } | null>(null);
  const [duplicateBusy, setDuplicateBusy] = useState(false);

  // Fetch active plans whenever the modal opens. Cleared on close so each
  // open-session gets a fresh list (admins can edit plans between opens).
  useEffect(() => {
    if (!props?.open) return;
    let cancelled = false;
    const loadPlans = async () => {
      setPlansLoading(true);
      try {
        const params = new URLSearchParams({ page: "1", pageSize: "200" });
        const res: any = await GET(
          `${API.SUBSCRIPTION_PLANS}?${params.toString()}`,
          null,
          null
        );
        const allPlans: Plan[] = Array.isArray(res?.data) ? res.data : [];
        const rows: Plan[] = filterPlansForViewer(allPlans, viewerRoleId);
        // TEMP deploy check — confirms this build has the free-plans-for-agents
        // fix. Remove once verified in production.
        console.log("[epaper-free-plans v2]", {
          rawRole: User?.user?.role,
          viewerRoleId,
          fetched: allPlans.map((p) => ({
            name: p.plan_name,
            price: p.price,
            allowed_role_ids: (p as any).allowed_role_ids,
          })),
          visibleToThisUser: rows.map((p) => p.plan_name),
        });
        if (cancelled) return;
        setPlans(rows);
        // Plan is mandatory — default to the first available active plan
        // so the form arrives in a valid, submit-ready state.
        if (rows.length > 0) {
          applyPlan(rows[0], rows);
        }
      } catch (err) {
        console.error("Failed to load subscription plans:", err);
        if (!cancelled) setPlans([]);
      } finally {
        if (!cancelled) setPlansLoading(false);
      }
    };
    loadPlans();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.open]);

  // Apply a plan's defaults to the form. Pulled out so the post-load
  // auto-select and the user-select handler share a single code path.
  const applyPlan = (plan: Plan, source?: Plan[], countryCode?: string) => {
    const list = source ?? plans;
    if (!plan || !list.find((p) => p.id === plan.id)) return;
    setSelectedPlanId(plan.id);
    const startCandidate = form.getFieldValue("subscription_start_date");
    const start: dayjs.Dayjs = dayjs.isDayjs(startCandidate)
      ? startCandidate
      : dayjs();
    const priced = resolvePlanPrice(
      plan,
      countryCode || form.getFieldValue("country") || defaultCountry
    );
    setCurrency(priced?.currency || getCountry(DEFAULT_COUNTRY).currency);
    form.setFieldsValue({
      subscription_plan_id: plan.id,
      // No price for this country → empty; the alert explains and the
      // backend refuses rather than charging AED.
      amount: priced?.missing ? undefined : priced?.amount ?? String(plan.price ?? 0),
      subscription_start_date: start,
      subscription_end_date: addPlanDuration(start, plan),
    });
  };

  const handleCountryChange = (code: string) => {
    const country = getCountry(code);
    const fields: Record<string, any> = {};
    // Countries with zones pick from their own list; others use the
    // country itself as the zone.
    if (countryHasZones(country.code)) {
      const zone = form.getFieldValue("zone");
      if (zone && !zonesForCountry(country.code).some((z) => z.name === zone)) {
        fields.zone = undefined;
      }
    } else {
      fields.zone = country.name;
    }
    // Follow the country with the dial code until a number is typed.
    if (!form.getFieldValue("phone")) fields.countryCode = country.dialCode;
    form.setFieldsValue(fields);
    const plan = plans.find((p) => p.id === selectedPlanId);
    if (plan) applyPlan(plan, plans, country.code);
    else setCurrency(country.currency);
  };

  // The form can open before E-Paper Settings finish loading, when only the
  // fallback countries exist. Once the agent's country becomes available,
  // select it — unless a country was already picked by hand.
  useEffect(() => {
    if (!props?.open || form.isFieldTouched("country")) return;
    if (form.getFieldValue("country") === defaultCountry) return;
    form.setFieldsValue({ country: defaultCountry });
    handleCountryChange(defaultCountry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.open, defaultCountry]);

  const handlePlanChange = (planId: number | null) => {
    if (planId == null) {
      // Plan is mandatory — keep the previous selection (Select isn't clearable
      // anymore). This branch only fires defensively.
      setSelectedPlanId(null);
      return;
    }
    const plan = plans.find((p) => p.id === planId);
    if (!plan) return;
    applyPlan(plan);
  };

  // Contact lookup. Passes email + phone to the identity service which
  // scans both up_users (agents/admins) and epaper_users (subscribers).
  // The epaper payload is rich so the confirm dialog can show full context
  // for identity verification without an extra round-trip.
  const checkUserExists = async (email?: string, phone?: string) => {
    try {
      const params = new URLSearchParams();
      if (email) params.append("email", email.trim().toLowerCase());
      if (phone) params.append("phone", phone.trim());
      const response = await axios.get(
        `${API.BASE_URL}${API.IDENTITY_CHECK}?${params.toString()}`
      );
      const data = response.data;

      return {
        exists: !!(data?.up || data?.epaper),
        up: data?.up || null,
        user: (data?.epaper || null) as ExistingEpaperUser | null,
      } as {
        exists: boolean;
        up: {
          id: number;
          username: string;
          email: string | null;
          phone_number: string | null;
          code: string | null;
          role?: { id: number; name: string } | null;
        } | null;
        user: ExistingEpaperUser | null;
      };
    } catch (error) {
      console.error("Error checking user:", error);
      throw error;
    }
  };

  function generateReadablePassword(length = 8) {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789"; // no confusing chars like lIO0
    let pass = "";
    for (let i = 0; i < length; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return pass;
  }

  const registerNewUser = async (
    values: any,
    opts?: { allowAgentConflict?: boolean }
  ) => {
    const new_phone = values.countryCode + values.phone;
    const newPassword = useDefaultPassword
      ? process.env.REACT_APP_DEFAULT_PASSWORD
      : values.password;

    const payload = {
      data: {
        full_name: values.full_name,
        email: values.email,
        phone: new_phone,
        zone: values.zone,
        country: values.country || defaultCountry,
        amount: values.amount,
        password: newPassword,
        confirm_password: newPassword,
        register_mode: "agent",
        created_by: User?.user?.id,
        role: values.role === "agent" ? "agent" : values.role === "epaper_admin" ? "epaper_admin" : null,
        payment_mode: values.payment_mode || "cash",
        payment_date: values.payment_date
          ? values.payment_date.toISOString()
          : new Date().toISOString(),
        subscription_start_date: values.subscription_start_date
          ? values.subscription_start_date.toISOString()
          : new Date().toISOString(),
        subscription_end_date: values.subscription_end_date
          ? values.subscription_end_date.toISOString()
          : undefined,
        subscription_plan_id:
          values.subscription_plan_id != null
            ? Number(values.subscription_plan_id)
            : null,
        // Lets the backend skip its agent-collision check — set only after
        // the admin has explicitly confirmed via the warning dialog below.
        allow_agent_conflict: !!opts?.allowAgentConflict,
      },
    };

    const api = API.BASE_URL + API.EPAPER_USERS_ADMIN_REGISTER;

    try {
      await axios.post(api, payload);
      message.success("Subscriber registered successfully.");
      form.resetFields();
      setSelectedPlanId(null);
      props?.refresh();
      return;
    } catch (err: any) {
      const data = err?.response?.data;
      // Fallback path: the backend register endpoint also runs an identity
      // check and returns the matched epaper user in the 409 payload. If the
      // up-front identity-check call failed (endpoint down, network hiccup,
      // etc.), we can still pivot into the upgrade dialog using this data
      // instead of leaving the admin staring at "already registered".
      if (err?.response?.status === 409 && data?.error?.details?.epaper_user) {
        const hit = data.error.details.epaper_user;
        setDuplicateMatch({
          user: hit as ExistingEpaperUser,
          typedEmail:
            typeof values.email === "string" ? values.email.trim().toLowerCase() : null,
          typedPhone: new_phone || null,
        });
        return;
      }
      message.error(
        data?.error?.message || "Could not register the subscriber. Please try again."
      );
      throw err;
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);

    try {
      const new_phone = values.countryCode + values.phone;
      const normalizedEmail =
        typeof values.email === "string" && values.email.trim().length > 0
          ? values.email.trim().toLowerCase()
          : undefined;

      // Cross-table identity check (epaper_users AND up_users).
      let checkResult;
      try {
        checkResult = await checkUserExists(normalizedEmail, new_phone);
      } catch (lookupErr) {
        // Non-fatal — backend will catch conflicts via its own checks
        // and the 409 path below surfaces them to the user.
        console.error("identity check failed, continuing:", lookupErr);
        checkResult = { exists: false, up: null, user: null };
      }

      // Case order matters: if this contact exists as a subscriber AND as
      // an agent (rare but possible — the two tables are independent), the
      // admin's intent when using the Create form is almost always to act
      // on the subscriber, so the subscriber match wins. Only when there's
      // no subscriber match do we fall through to the agent-collision warning.

      // Case 1: an epaper subscriber already exists for this email/phone.
      // Open the rich confirmation dialog — its contents adapt to the
      // subscription state (free, active, expired, suspended, partial match).
      if (checkResult?.exists && checkResult?.user) {
        setDuplicateMatch({
          user: checkResult.user,
          typedEmail: normalizedEmail || null,
          typedPhone: new_phone || null,
        });
        return;
      }

      // Case 2: no subscriber, but the contact is registered as an agent
      // in up_users. Block the create by default — the admin probably
      // mistyped — but allow override for the edge case of an agent who
      // is also personally subscribing.
      if (checkResult?.up) {
        const agent = checkResult.up;
        const emailCollision =
          normalizedEmail &&
          agent.email &&
          agent.email.toLowerCase() === normalizedEmail;
        const phoneCollision =
          new_phone && agent.phone_number === new_phone;

        if (emailCollision || phoneCollision) {
          await new Promise<void>((resolve) => {
            Modal.confirm({
              title: "Contact Belongs to an Agent Account",
              content: (
                <div>
                  <p>
                    The contact details you entered are already associated
                    with an agent account:
                  </p>
                  <p style={{ marginBottom: 4 }}>
                    <strong>{agent.username}</strong>
                    {agent.code ? ` (${agent.code})` : ""}
                  </p>
                  {agent.email && (
                    <p style={{ margin: 0 }}>Email: {agent.email}</p>
                  )}
                  {agent.phone_number && (
                    <p style={{ margin: 0 }}>Phone: {agent.phone_number}</p>
                  )}
                  <p style={{ marginTop: 12 }}>
                    Continue only if this agent is personally subscribing
                    under the same contact. Otherwise, cancel and use a
                    different email or phone number.
                  </p>
                </div>
              ),
              okText: "Continue and Create Subscription",
              cancelText: "Cancel",
              onOk: async () => {
                try {
                  await registerNewUser(values, { allowAgentConflict: true });
                } catch (err) {
                  console.error(err);
                  message.error(
                    "Could not register the subscriber. Please try again."
                  );
                } finally {
                  resolve();
                }
              },
              onCancel: () => resolve(),
            });
          });
          return;
        }
      }

      // Case 3: no conflict — register normally.
      await registerNewUser(values);
    } catch (err) {
      message.error("Could not register the subscriber. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Analyze the match so the confirm UI can (a) show warnings for partial
  // mismatches and (b) gate the action when it shouldn't proceed at all
  // (suspended users). Memoless — cheap enough to recompute on render.
  const matchAnalysis = (() => {
    if (!duplicateMatch) return null;
    const { user, typedEmail, typedPhone } = duplicateMatch;
    const emailMatches =
      !!typedEmail &&
      !!user.email &&
      user.email.toLowerCase() === typedEmail.toLowerCase();
    const phoneMatches = !!typedPhone && !!user.phone && user.phone === typedPhone;
    const emailMismatch = !!typedEmail && !!user.email && !emailMatches;
    const phoneMismatch = !!typedPhone && !!user.phone && !phoneMatches;
    const isSuspended = user.status === "suspended";
    const isInactive = user.status === "inactive";
    const subStatus = (user.subscription_status || "").toLowerCase();
    const hasActiveSubscription =
      user.isPaid &&
      !!user.subscription_end_date &&
      dayjs(user.subscription_end_date).isAfter(dayjs()) &&
      subStatus !== "cancelled" &&
      subStatus !== "refunded" &&
      subStatus !== "expired";
    return {
      emailMatches,
      phoneMatches,
      emailMismatch,
      phoneMismatch,
      isSuspended,
      isInactive,
      hasActiveSubscription,
      isRenewal: user.isPaid,
    };
  })();

  const closeDuplicateDialog = () => {
    if (duplicateBusy) return;
    setDuplicateMatch(null);
  };

  const confirmUpgradeExisting = async () => {
    if (!duplicateMatch || !matchAnalysis) return;
    if (matchAnalysis.isSuspended) return; // UI already disables the button
    setDuplicateBusy(true);
    try {
      // Hand off to the parent so it can close this create modal and open
      // the real UpgradeModal with this user pre-loaded. That path runs
      // the full subscription-creation flow, including the agent backfill.
      // Reset form state locally — we do NOT call props.close() here,
      // because the parent's close() clears the staged user that
      // onUpgradeExisting just set, racing UpgradeModal into an empty state.
      if (typeof props?.onUpgradeExisting === "function") {
        form.resetFields();
        setSelectedPlanId(null);
        setDuplicateMatch(null);
        props.onUpgradeExisting(duplicateMatch.user);
      } else {
        message.error(
          "Upgrade flow is not available. Please close this dialog and use the Upgrade action from the list."
        );
        setDuplicateMatch(null);
      }
    } finally {
      setDuplicateBusy(false);
    }
  };

  const formatDateDisplay = (val: string | null | undefined) => {
    if (!val) return "—";
    const d = dayjs(val);
    return d.isValid() ? d.format("DD-MMM-YYYY") : "—";
  };

  const describeRegisterMode = (mode: string | null | undefined) => {
    if (!mode) return "—";
    if (mode === "agent") return "Agent";
    if (mode === "paytabs") return "Paytabs";
    if (mode === "signup") return "Self signup";
    return mode;
  };

  const describeSubscriptionStatus = (user: ExistingEpaperUser) => {
    if (!user.isPaid) return { text: "Free (never paid)", color: "orange" };
    const status = (user.subscription_status || "").toLowerCase();
    if (status === "cancelled") return { text: "Cancelled", color: "red" };
    if (status === "refunded") return { text: "Refunded", color: "red" };
    if (status === "expired") return { text: "Expired", color: "red" };
    if (status === "paused") return { text: "Paused", color: "orange" };
    if (user.subscription_end_date) {
      const end = dayjs(user.subscription_end_date);
      if (end.isValid() && end.isBefore(dayjs()))
        return { text: "Expired", color: "red" };
      if (end.isValid())
        return { text: `Active until ${end.format("DD-MMM-YYYY")}`, color: "green" };
    }
    return { text: "Active", color: "green" };
  };

  const describeUserStatus = (status: string | null) => {
    if (status === "active") return { text: "Active", color: "green" };
    if (status === "inactive") return { text: "Inactive", color: "orange" };
    if (status === "suspended") return { text: "Suspended", color: "red" };
    return { text: status || "—", color: "default" };
  };

  return (
    <>
    <Modal
      width="95%"
      style={{ maxWidth: 900, top: 20 }}
      title="Register Epaper User"
      open={props?.open}
      onCancel={() => {
        setSelectedPlanId(null);
        props?.close();
      }}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          country: defaultCountry,
          zone: countryHasZones(defaultCountry) ? undefined : getCountry(defaultCountry).name,
          countryCode: getCountry(defaultCountry).dialCode,
          amount: "100",
          role: "User",
          payment_mode: "cash",
          payment_date: dayjs(),
          subscription_start_date: dayjs(),
          subscription_end_date: dayjs().add(1, "year"),
        }}
        style={{ marginTop: 16 }}
      >
        <Row>
          {/* ── LEFT COLUMN: User Details ── */}
          <Col xs={12} md={6} style={{ paddingRight: 8, paddingLeft: 8 }}>
            <div style={{ fontWeight: 600, marginBottom: 12, color: "#0576b7" }}>
              User Details
            </div>

            <Form.Item
              label="Full Name"
              name="full_name"
              rules={[{ required: true, message: "Full name is required" }]}
            >
              <Input allowClear placeholder="Enter full name" />
            </Form.Item>

            <Form.Item
              label="Country"
              name="country"
              rules={[{ required: true, message: "Please select a country" }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                onChange={handleCountryChange}
                options={activeCountries().map((c) => ({
                  value: c.code,
                  label: c.name,
                }))}
              />
            </Form.Item>

            <Form.Item label="Phone Number" required>
              {/* Percentage widths keep the country code + phone side-by-side
                  on every screen size (down to ~280px) without overflowing.
                  Input.Group compact merges the borders between the two. */}
              <Input.Group compact>
                <Form.Item
                  name="countryCode"
                  noStyle
                  rules={[dialCodeRule]}
                >
                  <DialCodeInput />
                </Form.Item>
                {/* Checked like the Add Agent form, against the selected code. */}
                <Form.Item
                  name="phone"
                  noStyle
                  dependencies={["countryCode"]}
                  normalize={normalizePhoneDigits}
                  rules={[phoneNumberRule()]}
                >
                  <Input
                    allowClear
                    style={{ width: "60%" }}
                    placeholder="XXXXXXXXX"
                  />
                </Form.Item>
              </Input.Group>
            </Form.Item>

            <Form.Item label="Email" name="email">
              <Input allowClear placeholder="Enter email" />
            </Form.Item>

            <Form.Item label="Zone" name="zone">
              <Select
                allowClear={hasZones}
                showSearch
                disabled={!hasZones}
                placeholder="Select zone"
                filterOption={(input, option) => {
                  if (!option) return false;
                  const text = Array.isArray(option.children)
                    ? option.children.join("")
                    : String(option.children || "");
                  return text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                }}
              >
                {hasZones
                  ? zonesForCountry(selectedCountry).map((z) => (
                      <Select.Option key={z.code} value={z.name}>
                        {z.name} ({z.code})
                      </Select.Option>
                    ))
                  : null}
              </Select>
            </Form.Item>

            <Form.Item style={{ marginBottom: 8 }}>
              <Checkbox
                checked={useDefaultPassword}
                onChange={(e) => setUseDefaultPassword(e.target.checked)}
              >
                Use Default Password: <strong>{process.env.REACT_APP_DEFAULT_PASSWORD}</strong>
              </Checkbox>
            </Form.Item>

            {!useDefaultPassword && (
              <Form.Item
                label="Password"
                name="password"
                rules={[{ required: true, message: "Password is required" }]}
              >
                <Input.Password allowClear placeholder="Enter password" />
              </Form.Item>
            )}
          </Col>

          {/* ── RIGHT COLUMN: Payment & Subscription ── */}
          {/* The left border is omitted because it becomes a jarring top
              border when the columns stack on mobile — the column header
              already provides the visual separation on every screen size. */}
          <Col xs={12} md={6} style={{ paddingLeft: 8, paddingRight: 8 }}>
            <div style={{ fontWeight: 600, marginBottom: 12, color: "#0576b7" }}>
              Payment & Subscription
            </div>

            <Form.Item
              label="Subscription Plan"
              name="subscription_plan_id"
              rules={[
                { required: true, message: "Please select a subscription plan" },
              ]}
            >
              <Select
                showSearch
                loading={plansLoading}
                placeholder={
                  plansLoading ? "Loading plans..." : "Select a plan"
                }
                onChange={(val: number | undefined) =>
                  handlePlanChange(val ?? null)
                }
                filterOption={(input, option) => {
                  const text = String((option as any)?.label ?? "");
                  return text.toLowerCase().includes(input.toLowerCase());
                }}
                options={plans.map((plan) => {
                  const price = Number(plan.price) || 0;
                  const priceLabel = price === 0 ? "Free" : `AED ${price}`;
                  const dur = `${plan.duration_value} ${
                    Number(plan.duration_value) === 1
                      ? plan.duration_type.replace(/s$/, "")
                      : plan.duration_type
                  }`;
                  const region = plan.region
                    ? plan.region === "gulf"
                      ? " · Gulf"
                      : " · India"
                    : "";
                  return {
                    value: plan.id,
                    label: `${plan.plan_name} - ${region}`,
                  };
                })}
                notFoundContent={
                  plansLoading ? "Loading..." : "No active plans"
                }
              />
            </Form.Item>

            <Form.Item
              label="Amount"
              name="amount"
              rules={[{ required: true, message: "Amount is required" }]}
            >
              <Input prefix={currency} placeholder="100" readOnly />
            </Form.Item>
            {planPrice?.missing ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={missingPriceMessage(selectedPlan, selectedCountry)}
              />
            ) : null}

            <Row>
              <Col md={6} style={{ paddingRight: 8 }}>
                <Form.Item label="Subscription Start" name="subscription_start_date">
                  <DatePicker
                    style={{ width: "100%" }}
                    format="DD-MM-YYYY"
                    placeholder="Start date"
                    inputReadOnly
                    open={false}
                    allowClear={false}
                  />
                </Form.Item>
              </Col>
              <Col md={6} style={{ paddingLeft: 8 }}>
                <Form.Item label="Subscription End" name="subscription_end_date">
                  <DatePicker
                    style={{ width: "100%" }}
                    format="DD-MM-YYYY"
                    placeholder="End date"
                    inputReadOnly
                    open={false}
                    allowClear={false}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Col md={6} style={{ paddingRight: 8 }}>
                <Form.Item
                  label="Payment Mode"
                  name="payment_mode"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Select placeholder="Select mode" allowClear>
                    {paymentModes.map((mode) => (
                      <Select.Option key={mode.value} value={mode.value}>
                        {mode.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col md={6} style={{ paddingLeft: 8 }}>
                <Form.Item label="Payment Date" name="payment_date">
                  <DatePicker
                    style={{ width: "100%" }}
                    format="DD-MM-YYYY"
                    placeholder="Select payment date"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* ── FOOTER BUTTONS ── */}
        <div
          style={{
            borderTop: "1px solid #f0f0f0",
            paddingTop: 16,
            marginTop: 8,
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
          }}
        >
          <Button
            onClick={() => {
              form.resetFields();
              setSelectedPlanId(null);
              props?.close();
            }}
            style={{ minWidth: 120 }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={!!planPrice?.missing}
            style={{ minWidth: 120 }}
          >
            Submit
          </Button>
        </div>
      </Form>
    </Modal>

      {/* ── Existing-subscriber detection dialog ───────────────────────
           Shown when checkUserExists finds a matching epaper_user row.
           Displays the full user record so the admin can positively
           identify the person before handing off to UpgradeModal.    */}
      {duplicateMatch && matchAnalysis && (() => {
        const { user, typedEmail, typedPhone } = duplicateMatch;
        const userStatus = describeUserStatus(user.status);
        const subStatus = describeSubscriptionStatus(user);
        const title = matchAnalysis.isSuspended
          ? "Account Suspended"
          : matchAnalysis.isRenewal
            ? "Existing Subscriber Found"
            : "Existing Free Subscriber Found";
        const confirmLabel = matchAnalysis.isSuspended
          ? "Cannot Proceed"
          : matchAnalysis.isRenewal
            ? "Continue to Renewal"
            : "Continue to Upgrade";
        return (
          <Modal
            width="95%"
            style={{ maxWidth: 960, top: 20 }}
            title={title}
            open={true}
            onCancel={closeDuplicateDialog}
            maskClosable={!duplicateBusy}
            closable={!duplicateBusy}
            footer={[
              <Button
                key="cancel"
                onClick={closeDuplicateDialog}
                disabled={duplicateBusy}
              >
                Cancel
              </Button>,
              <Button
                key="ok"
                type="primary"
                loading={duplicateBusy}
                disabled={matchAnalysis.isSuspended}
                onClick={confirmUpgradeExisting}
              >
                {confirmLabel}
              </Button>,
            ]}
          >
            {/* Context banner. Tone is formal. Every variant tells the
                admin exactly what Continue and Cancel do, so the
                decision is explicit and reversible. */}
            {matchAnalysis.isSuspended ? (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                message="This subscriber account is currently suspended."
                description="An upgrade or renewal cannot be performed while the account is suspended. Please reactivate the account from the subscribers list and try again."
              />
            ) : matchAnalysis.isRenewal && matchAnalysis.hasActiveSubscription ? (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={`${user.full_name || "This subscriber"} already has an active paid subscription.`}
                description="Click Continue to open the renewal form. The new term will be extended from the current subscription end date, so no paid days are lost. Click Cancel to return to the registration form and use a different email or phone number."
              />
            ) : matchAnalysis.isRenewal ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={`${user.full_name || "This subscriber"} previously had a paid subscription that is no longer active.`}
                description="Click Continue to open the renewal form. A new subscription term will start from today. Click Cancel to return to the registration form and use a different email or phone number."
              />
            ) : (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={`${user.full_name || "This subscriber"} is currently on a free account with no paid subscription on record.`}
                description="Click Continue to open the upgrade form and record the payment. Click Cancel to return to the registration form and use a different email or phone number."
              />
            )}

            {/* Mismatch warnings are shown only when the match is partial.
                Acting on the wrong match is the most costly error here,
                so surface this prominently. */}
            {(matchAnalysis.emailMismatch || matchAnalysis.phoneMismatch) && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="Please verify this is the intended subscriber."
                description={
                  <div>
                    {matchAnalysis.emailMismatch && (
                      <div>
                        The email you entered (<strong>{typedEmail}</strong>) does
                        not match the email on record (<strong>{user.email}</strong>).
                      </div>
                    )}
                    {matchAnalysis.phoneMismatch && (
                      <div>
                        The phone number you entered (<strong>{typedPhone}</strong>)
                        does not match the phone on record (<strong>{user.phone}</strong>).
                      </div>
                    )}
                    <div style={{ marginTop: 6 }}>
                      One identifier matched while the other did not. Please confirm
                      that this is the correct subscriber before continuing.
                    </div>
                  </div>
                }
              />
            )}

            {matchAnalysis.isInactive && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="This subscriber account is currently inactive."
                description="Proceeding with the upgrade will not automatically re-enable the account. Please review and activate the account from the subscribers list after completing the upgrade."
              />
            )}

            {/* Account + Subscription laid out side-by-side. Each column
                uses column={1} so labels and values stack vertically and
                readability stays intact at modal width. */}
            <Row>
              <Col md={6} style={{ paddingRight: 8 }}>
                <div style={{ fontWeight: 600, color: "#0576b7", marginBottom: 8 }}>
                  Account
                </div>
                <Descriptions bordered size="small" column={1}>
                  <Descriptions.Item label="Full Name">
                    {user.full_name || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Subscriber Code">
                    {user.subscriber_code || <span style={{ color: "#999" }}>Not yet assigned</span>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Account Status">
                    <Tag color={userStatus.color}>{userStatus.text}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {user.email || "—"}{" "}
                    {matchAnalysis.emailMatches && (
                      <Tag color="green" style={{ marginLeft: 4 }}>matches</Tag>
                    )}
                    {matchAnalysis.emailMismatch && (
                      <Tag color="orange" style={{ marginLeft: 4 }}>differs from input</Tag>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Phone">
                    {user.phone || "—"}{" "}
                    {matchAnalysis.phoneMatches && (
                      <Tag color="green" style={{ marginLeft: 4 }}>matches</Tag>
                    )}
                    {matchAnalysis.phoneMismatch && (
                      <Tag color="orange" style={{ marginLeft: 4 }}>differs from input</Tag>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Country">
                    {getCountry(user.country).name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Zone">
                    {user.zone || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Registered">
                    {formatDateDisplay(user.createdAt)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Register Mode">
                    {describeRegisterMode(user.register_mode)}
                  </Descriptions.Item>
                </Descriptions>
              </Col>

              <Col md={6} style={{ paddingLeft: 8 }}>
                <div style={{ fontWeight: 600, color: "#0576b7", marginBottom: 8 }}>
                  Subscription
                </div>
                <Descriptions bordered size="small" column={1}>
                  <Descriptions.Item label="Status">
                    <Tag color={subStatus.color}>{subStatus.text}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Current Plan">
                    {user.current_plan_type || <span style={{ color: "#999" }}>—</span>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Last Paid Amount">
                    {user.last_paid_amount != null
                      ? formatMoney(Number(user.last_paid_amount), user.currency)
                      : user.amount
                        ? formatMoney(user.amount, user.currency)
                        : <span style={{ color: "#999" }}>—</span>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Subscription Start">
                    {formatDateDisplay(user.subscription_start_date)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Subscription End">
                    {formatDateDisplay(user.subscription_end_date)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Current Agent">
                    {user.agent_name || user.agent_code ? (
                      <>
                        {user.agent_name || "—"}{" "}
                        {user.agent_code && (
                          <span style={{ color: "#0576b7", fontFamily: "monospace" }}>
                            ({user.agent_code})
                          </span>
                        )}
                      </>
                    ) : (
                      <span style={{ color: "#999" }}>
                        No agent has been attributed to this subscriber.
                      </span>
                    )}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
          </Modal>
        );
      })()}
    </>
  );
};

export default CreateModal;
